/**
 * Report Cache Module
 *
 * Provides UUID-based caching for reports using AsyncStorage.
 * Used to track which reports have been changed, commented on, etc.
 *
 * Performance: Uses direct getItem (O(1)) instead of getAllKeys + includes (O(n))
 * and Set for O(1) UUID lookups instead of Array.includes (O(n)).
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

// In-memory Set cache for O(1) lookups (populated on first access per cacheType)
const memoryCache = new Map();

/**
 * Get cache as a Set (with in-memory caching for O(1) lookups)
 * @param {string} cacheType - The type of cache
 * @returns {Promise<Set>} Set of cached UUIDs
 */
async function getCacheSet(cacheType) {
  // Return from memory cache if available
  if (memoryCache.has(cacheType)) {
    return memoryCache.get(cacheType);
  }

  try {
    const data = await AsyncStorage.getItem(cacheType);
    const cacheArray = data ? JSON.parse(data) : [];
    const cacheSet = new Set(cacheArray);
    memoryCache.set(cacheType, cacheSet);
    return cacheSet;
  } catch (error) {
    console.log('getCacheSet ERROR:', error);
    return new Set();
  }
}

/**
 * Persist cache Set to AsyncStorage
 * @param {string} cacheType - The type of cache
 * @param {Set} cacheSet - The Set to persist
 */
async function persistCache(cacheType, cacheSet) {
  try {
    await AsyncStorage.setItem(cacheType, JSON.stringify([...cacheSet]));
  } catch (error) {
    console.log('persistCache ERROR:', error);
  }
}

/**
 * Check if a report is in a specific cache
 * @param {string} uuid - The report UUID
 * @param {string} cacheType - The type of cache (e.g., 'changedReports', 'commentedReports')
 * @returns {Promise<boolean>} True if the report is cached
 */
export async function isReportCached(uuid, cacheType) {
  try {
    const cacheSet = await getCacheSet(cacheType);
    return cacheSet.has(uuid); // O(1) lookup
  } catch (error) {
    console.log('isReportCached ERROR:', error);
  }
  return false;
}

/**
 * Add or remove a report from a cache
 * @param {string} uuid - The report UUID
 * @param {string} cacheType - The type of cache
 * @param {boolean} keep - If true, add to cache; if false, remove from cache
 */
export async function setReportCache(uuid, cacheType, keep = true) {
  try {
    const cacheSet = await getCacheSet(cacheType);

    if (keep) {
      if (!cacheSet.has(uuid)) {
        cacheSet.add(uuid);
        await persistCache(cacheType, cacheSet);
      }
    } else {
      if (cacheSet.has(uuid)) {
        cacheSet.delete(uuid);
        await persistCache(cacheType, cacheSet);
      }
    }
  } catch (error) {
    console.log('setReportCache ERROR:', error);
  }
}
